import {Spinner} from '@patternfly/react-core';
import {Table, Tbody, Td, Th, Thead, Tr} from '@patternfly/react-table';
import {useEffect, useState} from 'react';
import {useRepositoryPermissions} from 'src/hooks/UseRepositoryPermissions';
import PermissionsToolbar from './PermissionsToolbar';
import {PermissionsColumnNames} from './ColumnNames';
import PermissionsDropdown from './PermissionsDropdown';
import {RepoMember} from 'src/resources/RepositoryResource';
import PermissionsKebab from './PermissionsKebab';
import {DrawerContentType} from '../Types';
import Entity from 'src/components/Entity';
import {EntityKind} from 'src/resources/UserResource';
import {usePaginatedSortableTable} from '../../../hooks/usePaginatedSortableTable';

export default function Permissions(props: PermissionsProps) {
  const {members, loading, error, search, setSearch} = useRepositoryPermissions(
    props.org,
    props.repo,
  );
  const [selectedMembers, setSelectedMembers] = useState<RepoMember[]>([]);

  const searchFilter =
    search.query !== ''
      ? (member: RepoMember) =>
          member.name.toLowerCase().includes(search.query.toLowerCase())
      : undefined;

  const {
    paginatedData: paginatedMembers,
    getSortableSort,
    paginationProps,
  } = usePaginatedSortableTable(members || [], {
    columns: {
      1: (item: RepoMember) => item.name, // Account
      2: (item: RepoMember) => item.type, // Type
      3: (item: RepoMember) => item.role, // Permissions
    },
    filter: searchFilter,
    initialPerPage: 20,
    initialSort: {columnIndex: 1, direction: 'asc'}, // Default sort: Account ascending
  });

  const onSelectMember = (
    member: RepoMember,
    rowIndex: number,
    isSelecting: boolean,
  ) => {
    setSelectedMembers((prevSelected) => {
      const others = prevSelected.filter((r) => r.name !== member.name);
      return isSelecting ? [...others, member] : others;
    });
  };

  // Close drawer if navigating away from permissions settings
  useEffect(() => {
    return () => {
      props.setDrawerContent(DrawerContentType.None);
    };
  }, []);

  if (loading) {
    return <Spinner />;
  }

  if (error) {
    return <>Unable to load permissions list</>;
  }

  return (
    <>
      <PermissionsToolbar
        org={props.org}
        repo={props.repo}
        allItems={members}
        paginatedItems={paginatedMembers}
        selectedItems={selectedMembers}
        page={paginationProps.page}
        setPage={paginationProps.setPage}
        perPage={paginationProps.perPage}
        setPerPage={paginationProps.setPerPage}
        searchOptions={[PermissionsColumnNames.account]}
        search={search}
        setSearch={setSearch}
        onItemSelect={onSelectMember}
        deselectAll={() => setSelectedMembers([])}
        setDrawerContent={props.setDrawerContent}
      />
      <Table aria-label="Repository permissions table" variant="compact">
        <Thead>
          <Tr>
            <Th />
            <Th sort={getSortableSort(1)}>{PermissionsColumnNames.account}</Th>
            <Th sort={getSortableSort(2)}>{PermissionsColumnNames.type}</Th>
            <Th sort={getSortableSort(3)}>
              {PermissionsColumnNames.permissions}
            </Th>
            <Th />
          </Tr>
        </Thead>
        <Tbody>
          {paginatedMembers?.map((member, rowIndex) => (
            <Tr key={member.name}>
              <Td
                select={{
                  rowIndex,
                  onSelect: (e, isSelecting) =>
                    onSelectMember(member, rowIndex, isSelecting),
                  isSelected: selectedMembers.some(
                    (r) => r.name === member.name,
                  ),
                }}
              />
              <Td data-label="membername">{member.name}</Td>
              <Td data-label="type">
                <Entity type={EntityKind[member.type]} includeText />
              </Td>
              <Td data-label="role">
                <PermissionsDropdown member={member} />
              </Td>
              <Td data-label="kebab">
                <PermissionsKebab member={member} />
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </>
  );
}

interface PermissionsProps {
  org: string;
  repo: string;
  setDrawerContent: (content: DrawerContentType) => void;
}
